import {
  AdgroupFormData,
  CampaignFormData,
  DefaultRecommendBehaviorTargetData,
  DefaultRecommendTargetTraceInfo,
  RecommendTargetTraceInfo,
  AdgroupConfig,
  BEHAVIOR_TARGETING_SCENES_TYPE,
} from '@tt4b/ttam-enums';
import {
  StateEnum,
  useRecommendTargetApi,
  useRequest,
  useStdAdgroupIs,
  useStore,
  useInterstKeywordsInfoApi,
} from '@tt4b/ttam-hooks';
import { computed, Ref, ref, watchEffect, ComputedRef, watch } from '@vue/composition-api';
import { useBehaviorOptions } from './../../../hooks/creation/adgroup/useBehavior/index';
import { isEmpty, uniqBy, cloneDeep, intersection, difference, isEqual } from 'lodash-es';
import { getCurrentLang, transformCountryToRecordArr } from '@tt4b/ttam-utils';
import { trackEvent } from './../../../utils/index';
import { RecommendTargetDataWithLabel } from './types';
import {
  useCommonInterestsInterestOptionsDataSource,
  handleLoadedInterests,
} from '../interests/hooks/common-interests-category';
import {
  recommendBehaviorTargetData,
  recommendInterestKeywordsList,
  originRecommendInterestKeywordsList,
} from './use-recomment-data';
import { useStdLocation } from './../location/hooks/index';

const recommendTargetTraceInfo: Ref<RecommendTargetTraceInfo> = ref(cloneDeep(DefaultRecommendTargetTraceInfo));

export function useRecommendInterestKeywordsDataSource(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  function loadRecommendInterestKeywordsInfo() {
    return useInterstKeywordsInfoApi({
      keywordInfos: recommendBehaviorTargetData.value.interestKeywords.map(keywordId => ({
        keyword_id: keywordId,
        lang: getCurrentLang(),
      })),
      industryTypes: [],
    }).then((keywordsInfo = []) => {
      const list = cloneDeep(keywordsInfo);
      const result = handleLoadedInterests(list, adgroupData);
      recommendInterestKeywordsList.value = result;
      originRecommendInterestKeywordsList.value = keywordsInfo;
    });
  }

  function resetInterestKeywords() {
    // 重新选择推荐的兴趣关键词时，需重置展示语言
    if (recommendInterestKeywordsList.value?.length) {
      handleLoadedInterests(recommendInterestKeywordsList.value, adgroupData, false);
    }
  }

  const recommendInterestKeywordsLabelMap = computed(() =>
    recommendInterestKeywordsList.value?.reduce((pre, item) => ({ ...pre, [item.keyword_id]: item.keyword }), {}),
  );
  const recommendInterestKeywordsItemMap = computed(() =>
    originRecommendInterestKeywordsList.value?.reduce((pre, item) => ({ ...pre, [item.keyword_id]: item }), {}),
  );
  return {
    loadRecommendInterestKeywordsInfo,
    recommendInterestKeywordsLabelMap,
    resetInterestKeywords,
    recommendInterestKeywordsItemMap,
  };
}

export function useRecommendTargetDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const { adgroupData, campaignData } = props;
  const { videoBehaviorMap, creatorBehaviorMap } = useBehaviorOptions({
    adgroupData,
    campaignData,
  });

  const { interestMap } = useCommonInterestsInterestOptionsDataSource({
    adgroupData,
    campaignData,
  });

  const { loadRecommendInterestKeywordsInfo, recommendInterestKeywordsLabelMap } =
    useRecommendInterestKeywordsDataSource({
      adgroupData,
    });

  const { isHasTikTok } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { fullCountryCodes } = useStdLocation({ adgroupData });
  const params = computed(() => ({
    countrys: fullCountryCodes.value.join(','),
    inventoryFlow: adgroupData.value.inventoryFlow,
    adRefAppId: adgroupData.value.adRefAppId,
  }));

  /** 过滤重名行为定向标签，临时处理定向侧推荐重名的问题，后续和 liuguangkuo 确认定向侧修复后移除 */
  const deprecatedFilterSameNameBehaviorTags = (behaviorIds: number[], labelMap: Record<number, string>) =>
    uniqBy(behaviorIds, id => labelMap?.[id]);

  const loadRecommendTarget = async () => {
    try {
      if (!params.value.countrys.length || !params.value.inventoryFlow.length) {
        recommendBehaviorTargetData.value = DefaultRecommendBehaviorTargetData;
        return;
      }

      const data = await useRecommendTargetApi(params.value);

      const filterAndSetData = () => {
        data.behavior.videoRelatedIds = isHasTikTok.value
          ? deprecatedFilterSameNameBehaviorTags(data.behavior.videoRelatedIds, videoBehaviorMap.value)
          : [];
        data.behavior.creatorRelatedIds = isHasTikTok.value
          ? deprecatedFilterSameNameBehaviorTags(data.behavior.creatorRelatedIds, creatorBehaviorMap.value)
          : [];
        recommendBehaviorTargetData.value = data;
      };

      if (Object.keys(videoBehaviorMap.value).length || Object.keys(creatorBehaviorMap.value).length) {
        filterAndSetData();
        await loadRecommendInterestKeywordsInfo();
        return;
      }

      const unwatch = watchEffect(() => {
        if (Object.keys(videoBehaviorMap.value).length || Object.keys(creatorBehaviorMap.value).length) {
          filterAndSetData();
          unwatch();
        }
      });
    } catch {
      recommendBehaviorTargetData.value = DefaultRecommendBehaviorTargetData;
    }
  };

  const { isLoading, error, run } = useRequest(loadRecommendTarget, {});

  watch(params, (val, oldVal) => {
    if (!isEqual(val, oldVal)) {
      loadRecommendTarget();
    }
  });
  const data = computed<RecommendTargetDataWithLabel>(() => ({
    behavior: {
      video:
        recommendBehaviorTargetData.value?.behavior?.videoRelatedIds?.map?.(o => ({
          value: o,
          label: videoBehaviorMap.value?.[o],
        })) ?? [],
      creator:
        recommendBehaviorTargetData.value?.behavior?.creatorRelatedIds?.map?.(o => ({
          value: o,
          label: creatorBehaviorMap.value?.[o],
        })) ?? [],
    },
    interest:
      recommendBehaviorTargetData.value?.interest?.map?.(o => ({ value: o, label: interestMap.value?.[o] })) ?? [],
    interestKeywords:
      recommendBehaviorTargetData.value?.interestKeywords?.map?.(o => ({
        value: o,
        label: recommendInterestKeywordsLabelMap.value?.[o],
      })) ?? [],
  }));

  return {
    data,
    isLoading,
    error,
    run,
  };
}

export function useRecommendVisible(opt: { adgroupConfig: Ref<AdgroupConfig>; campaignData: Ref<CampaignFormData> }) {
  const { adgroupConfig, campaignData } = opt;
  /** Get Data From Store  */
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  // 配置允许展示的情况下也要等待组件依赖的 areaMap 加载完成后再挂载
  const recommendVisible = computed(
    () =>
      adgroupConfig.value.showInterestRecommend && !isEmpty(areaMap.value) && !campaignData.value.industryTypes?.length,
  );
  return {
    recommendVisible,
  };
}

export function useRecommendTargetHas(options: { recommendData: Ref<RecommendTargetDataWithLabel> }) {
  const { recommendData } = options;

  const hasInterest = computed(() => recommendData?.value?.interest?.length);
  const hasVideoBehavior = computed(() => Boolean(recommendData?.value?.behavior.video?.length));
  const hasCreatorBehavior = computed(() => Boolean(recommendData?.value?.behavior.creator?.length));
  const hasBehavior = computed(() => hasVideoBehavior.value || hasCreatorBehavior.value);
  const hasInterestKeywords = computed(() => recommendData?.value?.interestKeywords?.length);

  return {
    hasInterest,
    hasBehavior,
    hasInterestKeywords,
    hasVideoBehavior,
    hasCreatorBehavior,
  };
}

export function useRecommendTargetTrack(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  recommendData?: ComputedRef<RecommendTargetDataWithLabel>;
}) {
  const { adgroupData } = options;

  const { data: recommendData } = useRecommendTargetDataSource(options);

  const { hasVideoBehavior, hasCreatorBehavior } = useRecommendTargetHas({
    recommendData,
  });

  function resetTraceInfo() {
    recommendTargetTraceInfo.value = cloneDeep(DefaultRecommendTargetTraceInfo);
  }

  function sendTraceEvent(adgroupIds: string | string[]) {
    const recommended = recommendBehaviorTargetData.value;
    const { adTagV2: selectedInterest, actionCategoriesV2, actionScenesV2, interestKeywordsI18n } = adgroupData.value;
    const selectedVideoBehavior = actionCategoriesV2[actionScenesV2.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE.video)];
    const selectedCreatorBehavior = actionCategoriesV2[actionScenesV2.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE.creator)];

    const adoptedInterest = intersection(selectedInterest, recommended.interest);
    const droppedInterest = difference(recommended.interest, adoptedInterest);
    const adoptedInterestKeywords = intersection(interestKeywordsI18n, recommended.interestKeywords);
    const droppedInterestKeywords = difference(recommended.interestKeywords, adoptedInterestKeywords);
    const adoptedBehavior: Record<number, number[]> = {};
    const droppedBehavior: Record<number, number[]> = {};
    if (hasVideoBehavior.value) {
      const recommendedVideoBehavior = recommended.behavior.videoRelatedIds;
      adoptedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.video] = intersection(
        selectedVideoBehavior,
        recommendedVideoBehavior,
      );
      droppedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.video] = difference(
        recommendedVideoBehavior,
        adoptedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.video],
      );
    }
    if (hasCreatorBehavior.value) {
      const recommendedCreatorBehavior = recommended.behavior.creatorRelatedIds;
      adoptedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.creator] = intersection(
        selectedCreatorBehavior,
        recommendedCreatorBehavior,
      );
      droppedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.creator] = difference(
        recommendedCreatorBehavior,
        adoptedBehavior[BEHAVIOR_TARGETING_SCENES_TYPE.creator],
      );
    }

    (Array.isArray(adgroupIds) ? adgroupIds : [adgroupIds]).forEach(adgroupId => {
      trackEvent('applied_targeting_recommendation_on_submitted', {
        adgroup_id: adgroupId,
        apply_outside_count: recommendTargetTraceInfo.value.applyThroughCounts.fromOutside,
        apply_from_learn_more_count: recommendTargetTraceInfo.value.applyThroughCounts.fromLearnMore,
        adopted: JSON.stringify({
          interest: adoptedInterest,
          behavior: adoptedBehavior,
          interestKeywords: adoptedInterestKeywords,
        }),
        dropped: JSON.stringify({
          interest: droppedInterest,
          behavior: droppedBehavior,
          interestKeywords: droppedInterestKeywords,
        }),
      });
    });

    resetTraceInfo();
  }

  function updateTraceInfo(from: 'fromOutside' | 'fromLearnMore' | 'fromSelectAll' | 'fromSingleTag') {
    const traceInfo = cloneDeep(recommendTargetTraceInfo.value);
    traceInfo.tracer = sendTraceEvent;
    traceInfo.applyThroughCounts[from]++;

    recommendTargetTraceInfo.value = traceInfo;
  }

  return {
    updateTraceInfo,
    sendTraceEvent,
    resetTraceInfo,
    recommendTargetTraceInfo,
    recommendData,
  };
}
